import { Component, EventEmitter, INJECTOR, Inject, Injector, Input, Output } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

/**
 * Base implementation of a form component that implements the ControlValueAccessor.
 * The goal of this class is to handle all ControlValueAccessor interactions as much as possible.
 * @template T The type of the value of the form component.
 */
@Component({
    template: '',
})
export abstract class BaseFormComponent<T> implements ControlValueAccessor {
    protected _value: T;
    public _control: NgControl;
    protected onChange: (newValue: T) => void;
    protected onTouched: () => void;

    get value(): T {
        return this._value;
    }

    @Input()
    set value(v: T) {
        if (this._value === v) {
            return;
        }
        this._value = v;
        this.valueChange.emit(v);
        if (this.onChange) {
            this.onChange(this._value);
        }
    }

    @Output() valueChange = new EventEmitter<T>();

    constructor(@Inject(INJECTOR) private injector: Injector) {}

    ngOnInit() {
        this._control = this.injector.get(NgControl);
    }

    writeValue(value: T): void {
        if (this._value === value) {
            return;
        }
        const isWriteable = this.onChange || (!this.onChange && value != null && typeof value != 'undefined');
        if (isWriteable) {
            this._value = value;
            this.valueChange.emit(value);
        }
    }

    registerOnChange(fn: (newValue: T) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    touched(): void {
        if (this.onTouched) {
            this.onTouched();
        }
    }
}
